"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { api, setSession } from "@/lib/api";
import { Alert, Button, Input, Spinner } from "@/components/ui";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { APP_VERSION } from "@/lib/version";
import type { LoginResponse } from "@/lib/types";

// Registration is centralised in the Hormuud Services hub — MySMS is sign-in only and links
// new users there.
const HORMUUD_URL = process.env.NEXT_PUBLIC_HORMUUD_URL || "http://localhost:3003";

// Password field with a show/hide toggle — a standard professional login affordance.
function PasswordInput({
  label,
  value,
  onChange,
  autoComplete,
  required,
  autoFocus,
  error,
  hint
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  required?: boolean;
  autoFocus?: boolean;
  error?: string;
  hint?: string;
}) {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === "function") setCaps(e.getModifierState("CapsLock"));
  };
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          onKeyUp={onKey}
          onKeyDown={onKey}
          onBlur={() => setCaps(false)}
          autoComplete={autoComplete}
          required={required}
          autoFocus={autoFocus}
          className={`w-full rounded-lg border bg-white px-3 py-2 pe-10 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-50 ${
            error
              ? "border-danger-400 focus:border-danger-500 focus:ring-danger-500/25"
              : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/25"
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 transition hover:text-gray-600"
        >
          {show ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.774 3.162 10.066 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </button>
      </div>
      {error ? (
        <span className="mt-1 block text-xs font-medium text-danger-600">{error}</span>
      ) : caps ? (
        <span className="mt-1 flex items-center gap-1 text-xs font-medium text-warning-600 dark:text-warning-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-3.5 w-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9l4 4H8l4-4zm-4 8h8" />
          </svg>
          Caps Lock is on
        </span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-gray-500">{hint}</span>
      ) : null}
    </label>
  );
}

// Playful SMS-themed showcase for the brand panel — a phone mockup mid-conversation
// with floating status chips, echoing the friendly composition of consumer sign-in pages.
function Showcase() {
  return (
    <div className="relative h-[26rem] w-[22rem]">
      {/* Phone mockup */}
      <div className="absolute left-1/2 top-1/2 w-56 -translate-x-1/2 -translate-y-1/2 rotate-[-4deg] rounded-[2rem] border border-gray-200 bg-white p-3 shadow-2xl">
        <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">HS</span>
          <div>
            <p className="text-[11px] font-semibold text-gray-900">Hormuud SMS</p>
            <p className="flex items-center gap-1 text-[9px] text-success-600"><span className="h-1.5 w-1.5 rounded-full bg-success-500" />Delivered</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="max-w-[80%] rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-[10px] text-gray-700">
            Your OTP code is 4821. Valid for 5 minutes.
          </div>
          <div className="ms-auto max-w-[85%] rounded-2xl rounded-br-md bg-brand-600 px-3 py-2 text-[10px] text-pure-white">
            Order #2291 is out for delivery 🚚
          </div>
          <div className="ms-auto flex items-center justify-end gap-1 pe-1 text-[9px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="h-3 w-3 text-success-500"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            Delivered · 16:45
          </div>
        </div>
      </div>

      {/* Floating chips */}
      <div className="absolute left-2 top-6 rotate-[-8deg] rounded-2xl bg-white px-3 py-2 shadow-lg ring-1 ring-gray-100">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-900">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success-100 text-success-600 dark:bg-success-500/20"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="h-3 w-3"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg></span>
          98.7% delivered
        </p>
      </div>
      <div className="absolute right-1 top-16 rotate-[7deg] rounded-full bg-accent-500 px-3 py-1.5 text-[11px] font-bold text-pure-white shadow-lg">
        1M+ / campaign
      </div>
      <div className="absolute bottom-10 left-0 rotate-[6deg] rounded-2xl bg-white px-3 py-2 shadow-lg ring-1 ring-gray-100">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-900">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4 w-4 text-brand-600"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
          Two-way inbox
        </p>
      </div>
      <div className="absolute bottom-16 right-2 flex h-11 w-11 rotate-[-6deg] items-center justify-center rounded-full bg-warning-400 text-lg shadow-lg">⚡</div>
    </div>
  );
}

type View = "credentials" | "otp" | "forgot" | "reset" | "register";

interface LoginResult extends Partial<LoginResponse> {
  twoFactorRequired?: boolean;
  username?: string;
  sentTo?: string | null;
}

export default function LoginPage() {
  const router = useRouter();
  const [view, setView] = useState<View>("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  // Live validation for the reset view: passwords must match (shown once the user starts typing).
  const confirmError = confirmPassword.length > 0 && confirmPassword !== newPassword ? "Passwords don't match." : "";

  function reset(next: View) {
    setError("");
    setInfo("");
    setCode("");
    setView(next);
  }

  // Apply a completed login session. This is the CUSTOMER portal: only USER-role accounts
  // may sign in here (admins use the admin portal).
  function finishLogin(res: LoginResponse): boolean {
    if (res.user.role !== "USER") {
      setError("This portal is for customer accounts. Administrators, please use the admin portal.");
      setLoading(false);
      return false;
    }
    setSession(res.token, res.user);
    router.replace("/dashboard");
    return true;
  }

  async function onCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api<LoginResult>("auth/login", { method: "POST", auth: false, body: { username, password } });
      if (res.twoFactorRequired) {
        setSentTo(res.sentTo ?? null);
        reset("otp");
        setLoading(false);
        return;
      }
      finishLogin(res as LoginResponse);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  }

  async function onOtp(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api<LoginResponse>("auth/login/verify", { method: "POST", auth: false, body: { username, code } });
      finishLogin(res);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  }

  async function onForgot(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api<{ sentTo: string | null; message: string }>("auth/password-reset/request", {
        method: "POST",
        auth: false,
        body: { username }
      });
      setSentTo(res.sentTo ?? null);
      reset("reset");
      setInfo(res.sentTo ? `A reset code was sent to ${res.sentTo}.` : res.message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function onReset(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api("auth/password-reset/verify", { method: "POST", auth: false, body: { username, code, newPassword } });
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      reset("credentials");
      setInfo("Password updated. You can now sign in.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const heading =
    view === "credentials" ? "Welcome back"
      : view === "otp" ? "Verify it's you"
      : view === "forgot" ? "Reset your password"
      : view === "register" ? "Request an account"
      : "Set a new password";

  const subtitle =
    view === "credentials" ? "Sign in to your customer account to continue."
      : view === "otp" ? "Enter the verification code to finish signing in."
      : view === "forgot" ? "We'll text a reset code to your registered mobile."
      : view === "register" ? "Tell us a bit about you — we'll get you set up."
      : "Choose a new password for your account.";

  return (
    <main className="flex min-h-screen flex-col bg-white">
      <div className="flex-1 lg:grid lg:grid-cols-2">
      {/* Brand showcase (desktop) — a light, friendly composition with a phone mockup. */}
      <div className="relative hidden overflow-hidden bg-gray-50 p-12 lg:flex lg:flex-col">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -right-16 top-20 h-72 w-72 rounded-full bg-brand-200/50 blur-3xl dark:bg-brand-500/10" />
          <div className="absolute -left-10 bottom-8 h-64 w-64 rounded-full bg-accent-200/50 blur-3xl dark:bg-accent-500/10" />
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-gray-200">
            <Logo className="h-full w-full" />
          </span>
          <span className="text-lg font-bold tracking-tight text-gray-900">Hormuud SMS</span>
        </div>

        <div className="relative z-10 flex flex-1 items-center justify-center py-6">
          <Showcase />
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-[2.75rem] font-extrabold leading-[1.05] tracking-tight text-gray-900">
            Reach every
            <br />
            customer,
            <br />
            <span className="text-brand-600">instantly.</span>
          </h2>
          <p className="mt-4 max-w-sm text-base text-gray-500">
            Bulk &amp; personalized SMS, real-time delivery reports, and a two-way inbox — all in one place.
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="relative flex min-h-screen items-center justify-center px-5 py-10 lg:min-h-0 lg:border-s lg:border-gray-100">
        <Link
          href="/"
          className="absolute start-4 top-4 inline-flex items-center gap-1 text-sm font-medium text-gray-500 transition hover:text-gray-800"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-4 w-4 rtl:rotate-180">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Home
        </Link>
        <div className="absolute end-4 top-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 p-2 ring-1 ring-brand-100">
              <Logo className="h-full w-full" />
            </span>
            <h1 className="mt-3 text-xl font-bold tracking-tight text-gray-900">Hormuud SMS</h1>
            <p className="text-sm text-gray-500">Customer Portal</p>
          </div>

          <div key={view} className="animate-slide-up">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">{heading}</h1>
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          </div>

          {view === "credentials" && (
            <form onSubmit={onCredentials} className="space-y-4">
              <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required />
              <PasswordInput label="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
              {info && <Alert kind="success">{info}</Alert>}
              <Alert>{error}</Alert>
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? <><Spinner /> Signing in…</> : "Log in"}
              </Button>
              <button type="button" onClick={() => reset("forgot")} className="block w-full text-center text-sm font-medium text-brand-600 hover:underline">
                Forgot password?
              </button>
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium text-gray-400">new to Hormuud?</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <a
                href={`${HORMUUD_URL}/login?view=register`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
              >
                Create your Hormuud account
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <p className="text-center text-xs text-gray-400">One Hormuud account signs you in to MySMS and every service.</p>
            </form>
          )}

          {view === "otp" && (
            <form onSubmit={onOtp} className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter the verification code{sentTo ? ` sent to ${sentTo}` : ""}.
              </p>
              <Input label="Verification code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoFocus required />
              <Alert>{error}</Alert>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Spinner /> Verifying…</> : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => reset("credentials")} className="block w-full text-center text-xs font-medium text-gray-500 hover:underline">
                Back to login
              </button>
            </form>
          )}

          {view === "forgot" && (
            <form onSubmit={onForgot} className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter your username. We&apos;ll text a reset code to the account&apos;s registered mobile.
              </p>
              <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
              <Alert>{error}</Alert>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Spinner /> Sending…</> : "Send reset code"}
              </Button>
              <button type="button" onClick={() => reset("credentials")} className="block w-full text-center text-xs font-medium text-gray-500 hover:underline">
                Back to login
              </button>
            </form>
          )}

          {view === "reset" && (
            <form onSubmit={onReset} className="space-y-4">
              {info && <Alert kind="success">{info}</Alert>}
              <Input label="Reset code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoFocus required />
              <PasswordInput label="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" hint="At least 8 characters" required />
              <PasswordInput label="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" error={confirmError} required />
              <Alert>{error}</Alert>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Spinner /> Updating…</> : "Reset password"}
              </Button>
              <button type="button" onClick={() => reset("credentials")} className="block w-full text-center text-xs font-medium text-gray-500 hover:underline">
                Back to login
              </button>
            </form>
          )}

          </div>

          <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            Secure sign-in · Two-factor supported
          </div>

        </div>
      </div>
      </div>

      {/* Footer — mirrors the language/link row on consumer sign-in pages. */}
      <footer className="border-t border-gray-100 py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-5 gap-y-1 px-4 text-xs text-gray-400">
          <Link href="/" className="hover:text-gray-600">Home</Link>
          <Link href="/guide" className="hover:text-gray-600">Guide</Link>
          <span>© {new Date().getFullYear()} Hormuud SMS</span>
          <span>Customer Portal · v{APP_VERSION}</span>
        </div>
      </footer>
    </main>
  );
}
